import type { ForqSupabaseClient } from './client';
import {
  normalizeSalesOfferInput,
  normalizeSalesOfferSearchInput,
  normalizeSalesOpportunityInput,
  normalizeSalesOpportunitySearchInput,
  normalizeSalesOrderSearchInput,
  type CompleteSalesHandoffInput,
  type CreateSalesDeliveryInput,
  type DecideSalesOfferInput,
  type SalesCustomerOption,
  type SalesGateway,
  type SalesHandoffRecord,
  type SalesItemOption,
  type SalesMachineDetail,
  type SalesMachineOption,
  type SalesOfferPage,
  type SalesOfferRecord,
  type SalesOfferSearchInput,
  type SalesOpportunityPage,
  type SalesOpportunityRecord,
  type SalesOpportunitySearchInput,
  type SalesOrderPage,
  type SalesOrderRecord,
  type SalesOrderSearchInput,
  type SalesOverview,
  type SaveSalesOfferInput,
  type SaveSalesOpportunityInput,
  type SaveSalesOrderDeliveryInput,
} from '@forq/sales-data';
import { SupabaseOperationError } from './errors';

function messageOf(error: { readonly message: string } | null): string {
  return error?.message ?? 'Unknown Supabase error.';
}

function object<T>(operation: string, value: unknown): T {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new SupabaseOperationError(operation, 'The RPC returned an invalid object.');
  }
  return value as T;
}

function array<T>(operation: string, value: unknown): readonly T[] {
  if (!Array.isArray(value)) throw new SupabaseOperationError(operation, 'The RPC returned an invalid list.');
  return value as readonly T[];
}

export class SupabaseSalesGateway implements SalesGateway {
  constructor(private readonly client: ForqSupabaseClient) {}

  async getOverview(tenantId: string): Promise<SalesOverview> {
    const { data, error } = await this.client.rpc('sales_overview', { p_tenant_id: tenantId });
    if (error) throw new SupabaseOperationError('sales.overview', messageOf(error));
    return object<SalesOverview>('sales.overview', data);
  }

  async searchCustomerOptions(tenantId: string, query = ''): Promise<readonly SalesCustomerOption[]> {
    const { data, error } = await this.client.rpc('sales_customer_options', {
      p_tenant_id: tenantId,
      p_query: query.trim(),
      p_limit: 50,
    });
    if (error) throw new SupabaseOperationError('sales.customers', messageOf(error));
    return array<SalesCustomerOption>('sales.customers', data ?? []);
  }

  async searchMachineOptions(tenantId: string, query = ''): Promise<readonly SalesMachineOption[]> {
    const { data, error } = await this.client.rpc('sales_machine_options', {
      p_tenant_id: tenantId,
      p_query: query.trim(),
      p_limit: 50,
    });
    if (error) throw new SupabaseOperationError('sales.machines', messageOf(error));
    return array<SalesMachineOption>('sales.machines', data ?? []);
  }

  async getMachineDetail(tenantId: string, machineId: string): Promise<SalesMachineDetail | null> {
    const { data, error } = await this.client.rpc('sales_machine_detail', {
      p_tenant_id: tenantId,
      p_machine_id: machineId,
    });
    if (error) throw new SupabaseOperationError('sales.machine.detail', messageOf(error));
    return data === null ? null : object<SalesMachineDetail>('sales.machine.detail', data);
  }

  async searchItemOptions(tenantId: string, query = ''): Promise<readonly SalesItemOption[]> {
    const { data, error } = await this.client.rpc('sales_item_options', {
      p_tenant_id: tenantId,
      p_query: query.trim(),
      p_limit: 50,
    });
    if (error) throw new SupabaseOperationError('sales.items', messageOf(error));
    return array<SalesItemOption>('sales.items', data ?? []);
  }

  async searchOpportunities(input: SalesOpportunitySearchInput): Promise<SalesOpportunityPage> {
    const normalized = normalizeSalesOpportunitySearchInput(input);
    const { data, error } = await this.client.rpc('sales_opportunity_search', {
      p_tenant_id: normalized.tenantId,
      p_query: normalized.query,
      p_stage: normalized.stage === 'all' ? null : normalized.stage,
      p_limit: normalized.pageSize,
      p_offset: (normalized.page - 1) * normalized.pageSize,
    });
    if (error) throw new SupabaseOperationError('sales.opportunities.search', messageOf(error));
    return object<SalesOpportunityPage>('sales.opportunities.search', data);
  }

  async getOpportunity(tenantId: string, opportunityId: string): Promise<SalesOpportunityRecord> {
    const { data, error } = await this.client.rpc('sales_opportunity_get', {
      p_tenant_id: tenantId,
      p_opportunity_id: opportunityId,
    });
    if (error) throw new SupabaseOperationError('sales.opportunities.get', messageOf(error));
    return object<SalesOpportunityRecord>('sales.opportunities.get', data);
  }

  async saveOpportunity(rawInput: SaveSalesOpportunityInput): Promise<SalesOpportunityRecord> {
    const input = normalizeSalesOpportunityInput(rawInput);
    const { data, error } = await this.client.rpc('sales_opportunity_save', {
      p_tenant_id: input.tenantId,
      p_payload: input,
    });
    if (error) throw new SupabaseOperationError('sales.opportunities.save', messageOf(error));
    return object<SalesOpportunityRecord>('sales.opportunities.save', data);
  }

  async searchOffers(input: SalesOfferSearchInput): Promise<SalesOfferPage> {
    const normalized = normalizeSalesOfferSearchInput(input);
    const { data, error } = await this.client.rpc('sales_offer_search', {
      p_tenant_id: normalized.tenantId,
      p_query: normalized.query,
      p_status: normalized.status === 'all' ? null : normalized.status,
      p_limit: normalized.pageSize,
      p_offset: (normalized.page - 1) * normalized.pageSize,
    });
    if (error) throw new SupabaseOperationError('sales.offers.search', messageOf(error));
    return object<SalesOfferPage>('sales.offers.search', data);
  }

  async getOffer(tenantId: string, offerId: string): Promise<SalesOfferRecord> {
    const { data, error } = await this.client.rpc('sales_offer_get', {
      p_tenant_id: tenantId,
      p_offer_id: offerId,
    });
    if (error) throw new SupabaseOperationError('sales.offers.get', messageOf(error));
    return object<SalesOfferRecord>('sales.offers.get', data);
  }

  async saveOffer(rawInput: SaveSalesOfferInput): Promise<SalesOfferRecord> {
    const input = normalizeSalesOfferInput(rawInput);
    const { data, error } = await this.client.rpc('sales_offer_save', {
      p_tenant_id: input.tenantId,
      p_payload: input,
    });
    if (error) throw new SupabaseOperationError('sales.offers.save', messageOf(error));
    return object<SalesOfferRecord>('sales.offers.save', data);
  }

  async decideOffer(input: DecideSalesOfferInput): Promise<SalesOrderRecord | null> {
    const { data, error } = await this.client.rpc('sales_offer_decide', {
      p_tenant_id: input.tenantId,
      p_offer_id: input.offerId,
      p_decision: input.decision,
      p_reason: input.reason?.trim() ?? '',
    });
    if (error) throw new SupabaseOperationError('sales.offers.decide', messageOf(error));
    return data === null ? null : object<SalesOrderRecord>('sales.offers.decide', data);
  }

  async searchOrders(input: SalesOrderSearchInput): Promise<SalesOrderPage> {
    const normalized = normalizeSalesOrderSearchInput(input);
    const { data, error } = await this.client.rpc('sales_order_search', {
      p_tenant_id: normalized.tenantId,
      p_query: normalized.query,
      p_limit: normalized.pageSize,
      p_offset: (normalized.page - 1) * normalized.pageSize,
    });
    if (error) throw new SupabaseOperationError('sales.orders.search', messageOf(error));
    return object<SalesOrderPage>('sales.orders.search', data);
  }

  async getOrder(tenantId: string, orderId: string): Promise<SalesOrderRecord> {
    const { data, error } = await this.client.rpc('sales_order_get', {
      p_tenant_id: tenantId,
      p_order_id: orderId,
    });
    if (error) throw new SupabaseOperationError('sales.orders.get', messageOf(error));
    return object<SalesOrderRecord>('sales.orders.get', data);
  }

  async listHandoffs(tenantId: string, limit = 100): Promise<readonly SalesHandoffRecord[]> {
    const { data, error } = await this.client.rpc('sales_handoff_list', {
      p_tenant_id: tenantId,
      p_limit: Math.max(1, Math.min(500, Math.trunc(limit))),
    });
    if (error) throw new SupabaseOperationError('sales.handoffs', messageOf(error));
    return array<SalesHandoffRecord>('sales.handoffs', data ?? []);
  }

  async completeHandoff(input: CompleteSalesHandoffInput): Promise<SalesHandoffRecord> {
    const { data, error } = await this.client.rpc('sales_handoff_complete', {
      p_tenant_id: input.tenantId,
      p_handoff_id: input.handoffId,
      p_result_number: input.resultNumber?.trim() ?? '',
    });
    if (error) throw new SupabaseOperationError('sales.handoffs.complete', messageOf(error));
    return object<SalesHandoffRecord>('sales.handoffs.complete', data);
  }

  async createDelivery(input: CreateSalesDeliveryInput): Promise<SalesOrderRecord> {
    const { data, error } = await this.client.rpc('sales_delivery_create', {
      p_tenant_id: input.tenantId,
      p_order_id: input.orderId,
    });
    if (error) throw new SupabaseOperationError('sales.delivery.create', messageOf(error));
    return object<SalesOrderRecord>('sales.delivery.create', data);
  }

  async confirmDelivery(input: CreateSalesDeliveryInput): Promise<SalesOrderRecord> {
    const { data, error } = await this.client.rpc('sales_delivery_confirm', {
      p_tenant_id: input.tenantId,
      p_order_id: input.orderId,
    });
    if (error) throw new SupabaseOperationError('sales.delivery.confirm', messageOf(error));
    return object<SalesOrderRecord>('sales.delivery.confirm', data);
  }

  async setDeliveryDetails(input: SaveSalesOrderDeliveryInput): Promise<SalesOrderRecord> {
    const { data, error } = await this.client.rpc('sales_order_set_delivery', {
      p_tenant_id: input.tenantId,
      p_order_id: input.orderId,
      p_payload: input.delivery,
    });
    if (error) throw new SupabaseOperationError('sales.order.delivery', messageOf(error));
    return object<SalesOrderRecord>('sales.order.delivery', data);
  }
}
